import {Account, AccountType} from '../../components/Search/SearchNav/types';
import {
  UsageType,
  useBillingPeriodSubscriptionsQuery,
} from '../../generated/graphql';
import {getInferenceSub, useAccountPrivileges} from '../accounts/pricing';
import {useRampFlagInferenceCatalog} from '../rampFeatureFlags';
import {
  formatOverageChargeText,
  getIntervalMarkersFromCurrentCalendarMonth,
  getIntervalMarkersFromLimitDateInterval,
  getIntervalMarkersFromSubscription,
  useGetUsageThisBillingPeriod,
  ZERO_COST_TEXT,
} from './utils';

/**
 * Hook to get inference usage details for an account.
 *
 * NOTE: This hook is for multi-tenant (SaaS) deployments only. Do NOT use in
 * single-tenant (server) deployments. For server deployments, use the hooks in
 * ServerCurrentBillingPeriodUsageSection.tsx.
 */
export function useGetInferenceUsageDetails(selectedAccount: Account | null) {
  const privilegeResults = useAccountPrivileges(selectedAccount);
  const {
    privileges: {inferenceLimitDateInterval, inferenceLimits: privilegeLimits},
  } = privilegeResults;
  const skip =
    selectedAccount?.name == null ||
    selectedAccount?.accountType === AccountType.Personal;
  const billingPeriodResults = useBillingPeriodSubscriptionsQuery({
    variables: {orgName: skip ? '' : selectedAccount.name},
    skip,
  });

  const stripeSubscription = getInferenceSub(
    billingPeriodResults.data?.organization ?? undefined
  );

  const enterpriseInferenceSubscription =
    billingPeriodResults.data?.organization?.inferenceEnterpriseSubscription;

  const isUsagePaid =
    stripeSubscription != null || enterpriseInferenceSubscription != null;

  const enterpriseLimitDateInterval =
    enterpriseInferenceSubscription != null
      ? {
          start: new Date(enterpriseInferenceSubscription.billingPeriodStart),
          end: new Date(enterpriseInferenceSubscription.billingPeriodEnd),
        }
      : undefined;

  const limits = {
    ...privilegeLimits,
  };
  const enterpriseFreeLimit =
    enterpriseInferenceSubscription?.inferenceLimit != null
      ? Number(BigInt(enterpriseInferenceSubscription?.inferenceLimit))
      : undefined;

  if (enterpriseFreeLimit != null) {
    limits.inferenceFreeLimit = enterpriseFreeLimit;
  }

  const finalLimitDateInterval =
    enterpriseLimitDateInterval ?? inferenceLimitDateInterval;

  const usagePeriod = selectedAccount?.isEnterprise
    ? getIntervalMarkersFromLimitDateInterval(finalLimitDateInterval)
    : getIntervalMarkersFromSubscription(stripeSubscription) ||
      getIntervalMarkersFromCurrentCalendarMonth();
  const usageResults = useGetUsageThisBillingPeriod(
    selectedAccount,
    UsageType.InferenceCost,
    usagePeriod,
    skip
  );

  const results = [billingPeriodResults, privilegeResults, usageResults];

  const loading = results.some(result => result.loading);
  const error = results.find(result => result?.error)?.error;
  const [billingPeriodStart, billingPeriodEnd] = usagePeriod;
  const {usage} = usageResults;
  const enabled = useRampFlagInferenceCatalog(selectedAccount?.name ?? '');

  // Calculate cost text
  let costText: string | undefined;
  if (isUsagePaid) {
    costText = ZERO_COST_TEXT;
    if (usage > limits.inferenceFreeLimit) {
      costText = formatOverageChargeText(
        (usage - limits.inferenceFreeLimit) / 100
      );
    }
  }

  return {
    billingPeriodStart,
    billingPeriodEnd,
    isUsagePaid,
    enabled,
    usage,
    limits,
    costText,
    loading,
    error,
  };
}
